import { observer, inject } from "mobx-react";
import { useState, useEffect } from "react";
import LoadingContainer from "../layouts/LoadingContainer";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";
import { Button, Modal, Text, TextArea, TextInput } from "../UI";
import { DatePicker, LocalizationProvider } from "@material-ui/pickers";
import DateFnsUtils from "@material-ui/pickers/adapter/date-fns";
import calendarImg from "../assets/img/calendar.svg";
import {
  Box,
  BoxHeader,
  InnerBox,
  SaveSuccess,
  ScrollDiv,
} from "../components/Common";
import { useInput, inputsHasError } from "../utils/hooks";
import { isNotEmpty, withError } from "../validation";
import { he } from "date-fns/locale";
import { format } from "date-fns";
import { maxLength } from "../validation/length";
import PhotosWidget from "../components/PhotosWidget";
import { ClipLoader } from "react-spinners";
import { useMobile } from "../utils/mobile";
import MessageNotification, {
  useMessageNotification,
  MessageType,
} from "../components/MessageNotification";

const NewNote = ({ userStore, tenantsStore }) => {
  const [isLoading, setLoading] = useState(false);
  const { t } = useTranslation("applies");
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMobile();

  const [newNote, setNewNote] = useState({
    CURDATE: new Date(),
    NAME: tenantsStore.selectedSiteSpace.NAME,
  });
  const [, setError] = useState("");
  const { messageNotification, setMessageNotification } =
    useMessageNotification();
  const [noteDecription, setNoteDecription] = useState("");
  const [extFiles, setExtFiles] = useState([]);
  const [department, setDepartment] = useState(location?.state?.department);
  const [user, setUser] = useState(location?.state?.user);
  const [type, setType] = useState(location?.state?.type);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isSaving, setSaving] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [topic, setTopic] = useState(null);
  const [status, setStatus] = useState(null);

  const handleDateChange = (date) => {
    var localDate = new Date(
      date.getTime() - date.getTimezoneOffset() * 60 * 1000
    );

    setNewNote({ ...newNote, CURDATE: localDate });
  };

  const handleDateClick = (event) => {
    setOpen((open) => !open);
    setAnchorEl(event.currentTarget);
  };

  const handleImgDateClick = (ref) => {
    setOpen((open) => !open);
  };

  useEffect(() => {
    setType(location?.state?.type);
    setUser(location?.state?.user);
    setDepartment(location?.state?.department);
  }, [location]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const data = {
        MAINCUST: tenantsStore.selectedSiteSpace.CUST,
        CUST: tenantsStore.selectedSiteSpace.CUST,
        MAINENV: tenantsStore.selectedSiteSpace.DNAME,
        DNAME: tenantsStore.selectedContract?.DNAME
      };
      const topics = (await tenantsStore.getNoteTopics(data)) || [];
      const statuses = (await tenantsStore.getNoteStatuses(data)) || [];
      const topic = topics?.find((t) => t.ZCOM_FROMTENPORTAL === "Y");
      const status = statuses?.find((s) => s.ZCOM_FROMTENPORTAL === "Y");
      setStatus(status);
      setTopic(topic);
      setLoading(false);
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddApply = async () => {
    const noteData = {
      ...newNote,
      USERLOGIN: user,
      CUSTNOTETYPEDES: type,
      CUSTNOTEEXTFILE_SUBFORM: extFiles,
      CUSTNOTESTEXT_SUBFORM: {
        TEXT: noteDecription.replaceAll("\n", "<br>") || "",
      },
      TOPICCODE: topic?.TOPICCODE,
      STATDES: status?.STATDES,
    };
    const body = {
      MAINCUST: tenantsStore.selectedSiteSpace.CUST,
      CUST: tenantsStore.selectedSiteSpace.CUST,
      MAINENV: userStore.userDetails.MAINENV,
      CONTNAME: tenantsStore.selectedContract?.CONTNAME,
      CONT: tenantsStore.selectedContract?.CONT,
      data: noteData,
      DNAME: tenantsStore.selectedContract?.DNAME,
    };

    if (!inputsHasError(date, description, applier, subject)) {
      setSaving(true);
      await tenantsStore.addSaleContractNote(body);
      setSaving(false);
      if (tenantsStore.error) {
        setError(tenantsStore.error);
        setMessageNotification({
          text: t(tenantsStore.error),
          show: true,
          type: MessageType.ERROR,
        });
      } else {
        setShowSuccessModal(true);
      }
    }
  };

  const handleRemoveImg = (file) => {
    const filteredExtFiles = extFiles.filter(
      (item) => item.EXTFILENAME !== file.EXTFILENAME
    );

    setExtFiles(filteredExtFiles);
  };

  const isNotEmptyWithError = withError(
    isNotEmpty,
    t("text_field_empty_error")
  );

  const maxLengthWithError = withError(
    (input) => maxLength(input, 120),
    t("text_field_note_length_error", { length: 120 })
  );

  const applier = useInput({
    validation: [isNotEmptyWithError],
    placeholder: t("applier_placeholder"),
    value: newNote?.NAME || "",
  });

  const date = useInput({
    validation: [isNotEmptyWithError],
    type: "TextArea",
    placeholder: t("date_placeholder"),
    value: format(
      newNote?.CURDATE !== undefined ? new Date(newNote.CURDATE) : new Date(),
      "dd/MM/yyyy"
    ),
  });

  const subject = useInput({
    validation: [isNotEmptyWithError],
    placeholder: t("subject_placeholder"),
    value: newNote?.SUBJECT || "",
  });

  const description = useInput({
    validation: [isNotEmptyWithError, maxLengthWithError],
    placeholder: t("description_placeholder"),
    value: newNote?.CUSTNOTESTEXT_SUBFORM?.TEXT || "",
  });

  const handleChange = (prop) => (event) => {
    setNewNote({ ...newNote, [prop]: event.target.value });
  };

  return (
    <>
      <LoadingContainer isLoading={isLoading}>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            margin: isMobile ? 0 : 25,
          }}
        >
          <Box flexBasis={100} width={"100%"} style={{}}>
            <InnerBox style={{ padding: 0, borderRadius: 18 }}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                }}
              >
                <Text.H5
                  color={theme.color.gray90}
                  style={{ textAlign: "start", padding: 20, paddingBottom: 0 }}
                >
                  {t("new_apply", { type: department })}
                </Text.H5>
                <div style={{ width: "100%", paddingInlineEnd: 10 }}>
                  <ScrollDiv
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 10,
                      marginTop: 20,
                      alignItems: "flex-start",
                      justifyContent: "flex-start",
                      flex: 1,
                      flexBasis: "100%",
                      borderTop: "1px solid",
                      borderBottom: "1px solid",
                      minHeight: "65vh",
                      height: "fit-content",
                      padding: 20,
                      borderColor: theme.color.gray30,
                    }}
                  >
                    <BoxHeader
                      style={{
                        display: "flex",
                        flexDirection: isMobile ? "column" : "row",
                        gap: 20,
                        alignItems: "center",
                        width: "100%",
                        padding: 0,
                        paddingBottom: 20,
                      }}
                    >
                      <div style={{ width: "100%" }}>
                        <LocalizationProvider
                          dateAdapter={DateFnsUtils}
                          locale={he}
                        >
                          <DatePicker
                            closeOnSelect={true}
                            mask="__/__/__"
                            inputFormat="dd/MM/yy"
                            value={selectedDate}
                            onChange={(date) => {
                              setSelectedDate(date);
                              handleDateChange(date);
                              setOpen(false);
                            }}
                            onClose={() => setOpen(false)}
                            open={open}
                            PopperProps={{
                              placement: "bottom-end",
                              positionFixed: true,
                              anchorEl: anchorEl,
                            }}
                            renderInput={({ inputRef, inputProps }) => (
                              <div
                                style={{
                                  flex: 1,
                                }}
                              >
                                <TextInput
                                  refProp={inputRef}
                                  divStyle={{
                                    display: "flex",
                                    padding: "10px 0",
                                    justifyContent: "flex-end",
                                    alignItems: "center",
                                    gap: 12,
                                  }}
                                  {...date}
                                  variant="outlined"
                                  label={t("apply_date")}
                                  onClick={handleDateClick}
                                  onEndImgClick={() => handleImgDateClick()}
                                  endImg={calendarImg}
                                  valid={!date.error}
                                  helperText={date.helperText}
                                ></TextInput>
                              </div>
                            )}
                          />
                        </LocalizationProvider>
                      </div>
                      <div style={{ width: "100%" }}>
                        <TextInput
                          divStyle={{
                            display: "flex",
                            padding: "10px 0",
                            justifyContent: "flex-end",
                            alignItems: "center",
                            gap: 12,
                            alignSelf: "stretch",
                          }}
                          {...applier}
                          variant="outlined"
                          onBlur={handleChange("NAME")}
                          onChange={handleChange("NAME")}
                          label={t("applier")}
                          valid={!applier.error}
                          helperText={applier.helperText}
                        ></TextInput>
                      </div>
                    </BoxHeader>
                    <div style={{ width: isMobile ? "100%" : "50%" }}>
                      <TextInput
                        divStyle={{
                          display: "flex",
                          padding: "10px 0",
                          justifyContent: "flex-end",
                          alignItems: "center",
                          gap: 12,
                        }}
                        {...subject}
                        variant="outlined"
                        onBlur={handleChange("SUBJECT")}
                        onChange={handleChange("SUBJECT")}
                        label={t("subject")}
                        helperText={subject.helperText}
                        valid={!subject.error}
                      ></TextInput>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: isMobile ? "column" : "row",
                        gap: 12,
                        marginTop: 20,
                        alignItems: "start",
                        justifyContent: "space-between",
                        width: "100%",
                      }}
                    >
                      <div style={{ flexBasis: isMobile ? "100%" : "50%" }}>
                        <TextArea
                          name="description"
                          rows={4}
                          cols={90}
                          onBlur={(e) => setNoteDecription(e.target.value)}
                          onChange={(e) => setNoteDecription(e.target.value)}
                          {...description}
                          variant="outlined"
                          label={t("description")}
                          multiple={true}
                          valid={!description.error}
                        ></TextArea>
                      </div>
                      <PhotosWidget
                        limit={5}
                        handleRemove={handleRemoveImg}
                        photosList={extFiles}
                        setPhotos={setExtFiles}
                        isMobile={isMobile}
                      />
                    </div>
                  </ScrollDiv>
                </div>
              </div>
              <div
                style={{
                  margin: "auto",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexDirection: "column",
                  width: "100%",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: isMobile ? "space-around" : "center",
                    alignItems: "center",
                    gap: 16,
                    width: "100%",
                    marginBottom: 30,
                  }}
                >
                  <Button
                    onClick={handleAddApply}
                    disabled={isSaving}
                    style={{
                      width: isMobile ? 140 : 181,
                      marginTop: isMobile ? 15 : 0,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: 10,
                      }}
                    >
                      {isSaving && (
                        <ClipLoader color={theme.color.white} size={20} />
                      )}
                      {t("send_apply")}
                    </div>
                  </Button>
                  <Button
                    onClick={() => navigate("/tenant/applies")}
                    style={{
                      backgroundColor: theme.color.white,
                      border: `1px solid ${theme.color.primary}`,
                      color: theme.color.primary,
                      marginTop: 0,
                      width: isMobile ? 110 : 181,
                    }}
                  >
                    {t("cancel_apply")}
                  </Button>
                </div>
              </div>
            </InnerBox>
          </Box>
        </div>
      </LoadingContainer>
      {showSuccessModal && (
        <Modal
          onClose={() => {
            setShowSuccessModal(false);
            navigate("/tenant/applies");
          }}
        >
          <SaveSuccess
            onButtonClick={() => navigate("/tenant/applies")}
            buttonLabel={t("applay_save_successs_button")}
            textLabel={t("applay_save_successs_text")}
          />
        </Modal>
      )}
      {messageNotification?.show && (
        <MessageNotification
          type={MessageType.ERROR}
          text={messageNotification?.text}
          onClose={() =>
            setMessageNotification({
              show: false,
            })
          }
        />
      )}
    </>
  );
};

export default inject("userStore", "tenantsStore")(observer(NewNote));
